import { Link } from "react-router-dom";
import { brandingConfig } from "../../config/branding";
import useUserStore from "../../stores/user";
import { useT } from "../../stores/uiLang";

// Run E2 J2: footer text in English and Spanish (shared UI language).
const FOOTER_T = {
  en: {
    tagline: "Bilingual, story-driven literacy for K-5 classrooms in English and Spanish.",
    quickLinks: "Quick Links",
    home: "Home",
    curriculum: "Curriculum",
    loginPortal: "Login Portal",
    supportLegal: "Support & Legal",
    support: "Support",
    faq: "FAQ",
    privacy: "Privacy Policy",
    ferpa: "FERPA Statement",
    aup: "Acceptable Use",
    dpa: "Data Processing Agreement",
    sopipa: "California SOPIPA",
    incident: "Incident Response",
    terms: "Terms of Service",
    accessibility: "Accessibility Statement",
    rights: "All rights reserved.",
    contact: "Contact",
    programBy: "A program by Startup Smartup",
  },
  es: {
    tagline: "Lectoescritura bilingüe basada en cuentos para salones de K a 5.º grado, en inglés y español.",
    quickLinks: "Enlaces rápidos",
    home: "Inicio",
    curriculum: "Currículo",
    loginPortal: "Iniciar sesión",
    supportLegal: "Ayuda y aviso legal",
    support: "Ayuda",
    faq: "Preguntas frecuentes",
    privacy: "Política de privacidad",
    ferpa: "Declaración FERPA",
    aup: "Uso aceptable",
    dpa: "Acuerdo de tratamiento de datos",
    sopipa: "SOPIPA de California",
    incident: "Respuesta a incidentes",
    terms: "Términos del servicio",
    accessibility: "Declaración de accesibilidad",
    rights: "Todos los derechos reservados.",
    contact: "Contacto",
    programBy: "Un programa de Startup Smartup",
  },
};

export function Footer() {
  const { profile } = useUserStore();
  const t = useT(FOOTER_T);
  const legalLinks: { to: string; label: string }[] = [
    { to: "/support", label: t.support },
    { to: "/faq", label: t.faq },
    { to: "/privacy", label: t.privacy },
    { to: "/ferpa", label: t.ferpa },
    { to: "/acceptable-use", label: t.aup },
    { to: "/legal/dpa", label: t.dpa },
    { to: "/legal/sopipa", label: t.sopipa },
    { to: "/legal/incident-response", label: t.incident },
    { to: "/terms", label: t.terms },
    { to: "/accessibility", label: t.accessibility },
  ];
  return (
    <footer className="footer-bg border-t border-[var(--color-footerPrimaryText)]">
      <div className="mx-auto max-w-6xl py-12 px-6 lg:py-16">
        <div className="grid grid-cols-1 gap-8 xl:grid-cols-5">
          <div className="xl:col-span-2">
            <div className="flex items-center">
              <img
                src={brandingConfig.logoPath}
                alt={brandingConfig.siteTitle}
                className=" h-10 w-38"
              />
            </div>
            <p className="mt-4 text-base footer-primary-text max-w-md">
              {t.tagline}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 xl:col-span-3">
            <div>
              <h3 className="text-lg font-bold footer-primary-text">
                {t.quickLinks}
              </h3>
              <ul className="mt-4 space-y-3">
                <li>
                  <Link
                    to="/"
                    className="text-sm footer-secondary-text hover:opacity-75"
                  >
                    {t.home}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/standards"
                    className="text-sm footer-secondary-text hover:opacity-75"
                  >
                    {t.curriculum}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/login-portal"
                    className="text-sm footer-secondary-text hover:opacity-75"
                  >
                    {t.loginPortal}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-bold footer-primary-text">
                {t.supportLegal}
              </h3>
              {/* Run E2 J2: wraps on phones (was nowrap in 2 fixed columns,
                  which pushed the page sideways at 390 px). */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 footer-secondary-text">
                {legalLinks.map((l) => (
                  <Link key={l.to} to={l.to} className="text-sm hover:opacity-75">
                    {l.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 border-t border-[var(--color-footerPrimaryText)] pt-8 flex flex-col md:flex-row items-center justify-between">
          <p className="text-sm footer-primary-text text-center">
            &copy; {new Date().getFullYear()} {brandingConfig.siteTitle}.{" "}
            {t.rights}
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mt-4 md:mt-0 footer-secondary-text">
            <Link to="/privacy" className="text-sm hover:opacity-75">
              {t.privacy}
            </Link>
            <Link to="/terms" className="text-sm hover:opacity-75">
              {t.terms}
            </Link>
            {(!profile || profile.role !== "student") && (
              <Link to="/contact" className="text-sm hover:opacity-75">
                {t.contact}
              </Link>
            )}
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center">
          <div className="flex flex-wrap items-center justify-center gap-x-2 text-sm text-gray-500">
            <span>{t.programBy}</span>
            <span>•</span>
            <a
              href="mailto:hello@startupsmartup.com"
              className="hover:text-green-700"
            >
              hello@startupsmartup.com
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
